import { CalendarRange, X } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import type {
  GalleryFilterOptions,
  GalleryFilters,
} from "@/types/content-gallery"

/**
 * OKS-39 — Filter rail for `/brand/content`.
 *
 * Three controls + Reset (UX Spec §3.1):
 *   1. Mission `Select` — `filterOptions.missions`. Default "All missions".
 *   2. Creator `Select` — `filterOptions.creators` (`@handle` label).
 *   3. Date range `Popover` — two native `<input type="date">` (KD-8 +
 *      UQ-2: native input, not shadcn `Calendar`). Either bound nullable.
 *   4. Reset `Button` (ghost) — visible iff ≥1 filter active.
 *
 * Wire format: every change propagates via `onFilterChange(key, value)`.
 * The hook owns `router.get` partial-reload; this component is pure UI.
 */
const ALL_VALUE = "__all__"

interface GalleryFiltersProps {
  filterOptions: GalleryFilterOptions
  applied: GalleryFilters
  onFilterChange: <K extends keyof GalleryFilters>(
    key: K,
    value: GalleryFilters[K],
  ) => void
  onResetAll: () => void
  className?: string
}

export function GalleryFilters({
  filterOptions,
  applied,
  onFilterChange,
  onResetAll,
  className,
}: GalleryFiltersProps) {
  const { t } = useTranslation()
  const showClear = hasActiveFilter(applied)

  return (
    <div
      data-testid="gallery-filter-bar"
      className={cn(
        "flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center",
        "bg-background sticky top-0 z-10 border-b py-3 sm:static sm:border-0 sm:py-0",
        className,
      )}
    >
      <MissionFilter
        options={filterOptions.missions}
        value={applied.mission_id}
        onChange={(v) => onFilterChange("mission_id", v)}
      />
      <CreatorFilter
        options={filterOptions.creators}
        value={applied.creator_id}
        onChange={(v) => onFilterChange("creator_id", v)}
      />
      <DateRangeFilter
        from={applied.from}
        to={applied.to}
        onChange={(next) => {
          onFilterChange("from", next.from)
          // `from` triggers a render with page=1; `to` follows on its own
          // change. Two router.get calls are acceptable — partial reloads
          // are cheap and Inertia coalesces back-to-back navigations.
          if (next.toChanged) onFilterChange("to", next.to)
        }}
      />
      {showClear && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onResetAll}
          aria-label={t("content_gallery.filter.reset_aria")}
          data-testid="gallery-filter-reset"
        >
          <X aria-hidden className="mr-1 size-4" />
          {t("content_gallery.filter.reset")}
        </Button>
      )}
    </div>
  )
}

function MissionFilter({
  options,
  value,
  onChange,
}: {
  options: GalleryFilterOptions["missions"]
  value: number | null
  onChange: (next: number | null) => void
}) {
  const { t } = useTranslation()
  const selectValue = value == null ? ALL_VALUE : String(value)

  return (
    <div className="flex w-full flex-col gap-1 sm:w-auto">
      <Label className="sr-only" htmlFor="gallery-filter-mission">
        {t("content_gallery.filter.mission.aria")}
      </Label>
      <Select
        items={[
          { value: ALL_VALUE, label: t("content_gallery.filter.mission.all") },
          ...options.map((mission) => ({
            value: String(mission.id),
            label: mission.title,
          })),
        ]}
        value={selectValue}
        onValueChange={(raw) => {
          if (raw === ALL_VALUE) {
            onChange(null)
            return
          }
          const id = Number(raw)
          onChange(Number.isFinite(id) ? id : null)
        }}
      >
        <SelectTrigger
          id="gallery-filter-mission"
          aria-label={t("content_gallery.filter.mission.aria")}
          data-testid="gallery-filter-mission"
          className="min-w-[12rem]"
        >
          <SelectValue placeholder={t("content_gallery.filter.mission.all")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>
            {t("content_gallery.filter.mission.all")}
          </SelectItem>
          {options.map((mission) => (
            <SelectItem key={mission.id} value={String(mission.id)}>
              {mission.title}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function CreatorFilter({
  options,
  value,
  onChange,
}: {
  options: GalleryFilterOptions["creators"]
  value: number | null
  onChange: (next: number | null) => void
}) {
  const { t } = useTranslation()
  const selectValue = value == null ? ALL_VALUE : String(value)

  return (
    <div className="flex w-full flex-col gap-1 sm:w-auto">
      <Label className="sr-only" htmlFor="gallery-filter-creator">
        {t("content_gallery.filter.creator.aria")}
      </Label>
      <Select
        items={[
          { value: ALL_VALUE, label: t("content_gallery.filter.creator.all") },
          ...options.map((creator) => ({
            value: String(creator.id),
            label: creator.handle ? `@${creator.handle}` : creator.name,
          })),
        ]}
        value={selectValue}
        onValueChange={(raw) => {
          if (raw === ALL_VALUE) {
            onChange(null)
            return
          }
          const id = Number(raw)
          onChange(Number.isFinite(id) ? id : null)
        }}
      >
        <SelectTrigger
          id="gallery-filter-creator"
          aria-label={t("content_gallery.filter.creator.aria")}
          data-testid="gallery-filter-creator"
          className="min-w-[12rem]"
        >
          <SelectValue placeholder={t("content_gallery.filter.creator.all")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>
            {t("content_gallery.filter.creator.all")}
          </SelectItem>
          {options.map((creator) => (
            <SelectItem key={creator.id} value={String(creator.id)}>
              {creator.handle ? `@${creator.handle}` : creator.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

interface DateRangeChange {
  from: string | null
  to: string | null
  toChanged: boolean
}

function DateRangeFilter({
  from,
  to,
  onChange,
}: {
  from: string | null
  to: string | null
  onChange: (next: DateRangeChange) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const triggerLabel =
    from || to
      ? formatRangeLabel(from, to)
      : t("content_gallery.filter.date.anytime")

  const onClear = () => {
    onChange({ from: null, to: null, toChanged: true })
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            aria-label={t("content_gallery.filter.date.aria")}
            data-testid="gallery-filter-date"
            className="min-w-[10rem] justify-start font-normal"
          >
            <CalendarRange aria-hidden className="mr-2 size-4" />
            {triggerLabel}
          </Button>
        }
      />
      <PopoverContent
        className="flex w-[18rem] flex-col gap-3"
        data-testid="gallery-filter-date-popover"
      >
        <div className="flex flex-col gap-1">
          <Label htmlFor="gallery-filter-date-from">
            {t("content_gallery.filter.date.from")}
          </Label>
          <input
            id="gallery-filter-date-from"
            type="date"
            value={from ?? ""}
            onChange={(e) =>
              onChange({
                from: e.target.value === "" ? null : e.target.value,
                to,
                toChanged: false,
              })
            }
            className="border-input h-9 rounded-md border bg-transparent px-3 text-sm"
            data-testid="gallery-filter-date-from"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="gallery-filter-date-to">
            {t("content_gallery.filter.date.to")}
          </Label>
          <input
            id="gallery-filter-date-to"
            type="date"
            value={to ?? ""}
            onChange={(e) =>
              onChange({
                from,
                to: e.target.value === "" ? null : e.target.value,
                toChanged: true,
              })
            }
            className="border-input h-9 rounded-md border bg-transparent px-3 text-sm"
            data-testid="gallery-filter-date-to"
          />
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClear}
          data-testid="gallery-filter-date-clear"
        >
          {t("content_gallery.filter.date.clear")}
        </Button>
      </PopoverContent>
    </Popover>
  )
}

function formatRangeLabel(from: string | null, to: string | null): string {
  if (from && to) return `${from} → ${to}`
  if (from) return `${from} →`
  if (to) return `→ ${to}`
  return ""
}

/** True iff at least one filter is non-default. */
export function hasActiveFilter(applied: GalleryFilters): boolean {
  return (
    applied.mission_id != null ||
    applied.creator_id != null ||
    applied.from != null ||
    applied.to != null
  )
}
